// Schedule template editor: admin UI to create/edit/archive named schedule
// templates and their blocks (time windows + appointment-type allow/exclude
// lists). Mirrors StaffRotationGrid's per-row local-edit-state pattern — each
// template row owns its own draft state, so editing or saving one template
// never disturbs another.
import { ChevronRight, Plus, X } from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'

import {
  APPOINTMENT_TYPES,
  type AppointmentTypeId,
  type ScheduleBlock,
  type ScheduleTemplate,
  scheduleBlocksSchema,
} from '@snootsvet/shared'

import { Button } from '@/frontend/components/ui/button'
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/frontend/components/ui/collapsible'
import { ConfirmButton } from '@/frontend/components/ui/confirm-button'
import { Input } from '@/frontend/components/ui/input'
import { MultiSelectFilter } from '@/frontend/components/ui/multi-select-filter'
import { ToggleGroup, ToggleGroupItem } from '@/frontend/components/ui/toggle-group'
import { useViewerRegion } from '@/frontend/features/region/region-context'
import {
  useCreateScheduleTemplate,
  useSetScheduleTemplateArchived,
  useUpdateScheduleTemplate,
} from '@/frontend/features/schedules/use-schedule-template-mutations'
import { useScheduleTemplates } from '@/frontend/features/schedules/use-schedule-templates'

/** Options for the appointment-type multiselect, shared by every block row. */
const APPOINTMENT_TYPE_OPTIONS = APPOINTMENT_TYPES.map((t) => ({ value: t.id, label: t.name }))

/** A fresh block for "Add block": open 9-5, allowlist mode, no restrictions (all types allowed). */
function blankBlock(): ScheduleBlock {
  return { name: '', startTime: '09:00', endTime: '17:00', appointmentTypesAllowed: [] }
}

/**
 * A block paired with a client-only React key. `ScheduleBlock` has no id of its
 * own, and array index isn't a safe key once blocks can be reordered by removal
 * (an editor mid-block would silently jump to a different block's data) — so
 * each draft gets a stable key for the lifetime of this row's local state.
 */
interface BlockDraft {
  key: string
  block: ScheduleBlock
}

function toBlockDrafts(blocks: ScheduleBlock[]): BlockDraft[] {
  return blocks.map((block) => ({ key: crypto.randomUUID(), block }))
}

export function ScheduleTemplateEditor() {
  const { data: templates = [] } = useScheduleTemplates()
  // Whether the "Add template" draft row is showing.
  const [adding, setAdding] = useState(false)
  const [showArchived, setShowArchived] = useState(false)

  const active = templates.filter((t) => t.archivedAt === null)
  const archived = templates.filter((t) => t.archivedAt !== null)

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-end">
        <Button variant="outline" onClick={() => setAdding(true)} disabled={adding}>
          <Plus className="size-4" />
          Add template
        </Button>
      </div>

      <div className="flex flex-col gap-2">
        {adding && (
          <TemplateRow
            defaultOpen
            onCreated={() => setAdding(false)}
            onCancelCreate={() => setAdding(false)}
          />
        )}

        {active.map((template) => (
          <TemplateRow key={template.id} template={template} />
        ))}

        {active.length === 0 && !adding && (
          <p className="text-muted-foreground">
            No templates yet. Use “Add template” to create one.
          </p>
        )}
      </div>

      {archived.length > 0 && (
        <div className="flex flex-col gap-2">
          <Button variant="ghost" className="w-fit" onClick={() => setShowArchived((v) => !v)}>
            {showArchived ? 'Hide' : 'Show'} archived ({archived.length})
          </Button>
          {showArchived &&
            archived.map((template) => <TemplateRow key={template.id} template={template} />)}
        </div>
      )}
    </div>
  )
}

interface TemplateRowProps {
  /** The template to edit; omitted while this row is drafting a new one. */
  template?: ScheduleTemplate
  defaultOpen?: boolean
  /** Fired after a successful create, so the parent can drop the draft row. */
  onCreated?: () => void
  /** Fired when the user cancels an in-progress create. */
  onCancelCreate?: () => void
}

/** A single template's collapsible editor: name, block list, Save, and Archive/Unarchive. */
function TemplateRow({ template, defaultOpen, onCreated, onCancelCreate }: TemplateRowProps) {
  const isCreating = template === undefined

  // Seed local edit state once from props; the row owns it from then on so a
  // sibling save / background refetch never clobbers in-progress edits.
  const [name, setName] = useState(template?.name ?? '')
  const [blockDrafts, setBlockDrafts] = useState<BlockDraft[]>(() =>
    toBlockDrafts(structuredClone(template?.blocks ?? [blankBlock()]))
  )
  const [dirty, setDirty] = useState(false)

  const { region } = useViewerRegion()
  const create = useCreateScheduleTemplate()
  const update = useUpdateScheduleTemplate()
  const setArchived = useSetScheduleTemplateArchived()

  const setBlock = (index: number, block: ScheduleBlock) => {
    setBlockDrafts((prev) => prev.map((d, i) => (i === index ? { ...d, block } : d)))
    setDirty(true)
  }

  const addBlock = () => {
    setBlockDrafts((prev) => [...prev, { key: crypto.randomUUID(), block: blankBlock() }])
    setDirty(true)
  }

  const removeBlock = (index: number) => {
    setBlockDrafts((prev) => prev.filter((_, i) => i !== index))
    setDirty(true)
  }

  const blocks = blockDrafts.map((d) => d.block)

  const onSave = () => {
    if (isCreating) {
      create.mutate(
        { region, name, blocks },
        {
          onSuccess: () => onCreated?.(),
          onError: (err) =>
            toast.error(err instanceof Error ? err.message : 'Failed to create template'),
        }
      )
    } else {
      update.mutate(
        { id: template.id, body: { name, blocks } },
        {
          onSuccess: () => setDirty(false),
          onError: (err) =>
            toast.error(err instanceof Error ? err.message : 'Failed to save template'),
        }
      )
    }
  }

  const toggleArchived = () => {
    if (!template) return
    setArchived.mutate(
      { id: template.id, archived: template.archivedAt === null },
      {
        onError: (err) =>
          toast.error(err instanceof Error ? err.message : 'Failed to update template'),
      }
    )
  }

  const isSaving = create.isPending || update.isPending
  // Gate Save on the same schema the server enforces (≥1 block, each with a name, valid
  // HH:MM times, and startTime < endTime) for a clear disabled state instead of a 400 toast.
  const blocksValid = scheduleBlocksSchema.safeParse(blocks).success
  const canSave = name.trim().length > 0 && blocksValid && (isCreating || dirty)

  return (
    <Collapsible defaultOpen={defaultOpen} className="rounded-md border">
      <div className="flex items-center justify-between gap-2 px-3 py-2">
        <CollapsibleTrigger className="group flex items-center gap-2 font-medium">
          <ChevronRight className="size-4 text-muted-foreground transition-transform group-aria-expanded:rotate-90" />
          {isCreating ? name || 'New template' : template.name}
        </CollapsibleTrigger>
        <div className="flex items-center gap-2">
          {isCreating ? (
            <Button variant="ghost" onClick={onCancelCreate}>
              Cancel
            </Button>
          ) : template.archivedAt === null ? (
            <ConfirmButton
              variant="ghost"
              heading="Archive template?"
              description="It will no longer be offered for rotas or shifts. Rotas and shifts already using it keep their hours."
              confirmLabel="Archive"
              confirmPending={setArchived.isPending}
              onConfirm={toggleArchived}
            >
              Archive
            </ConfirmButton>
          ) : (
            <Button variant="ghost" disabled={setArchived.isPending} onClick={toggleArchived}>
              Unarchive
            </Button>
          )}
          <Button disabled={!canSave || isSaving} onClick={onSave}>
            {isSaving ? 'Saving...' : 'Save'}
          </Button>
        </div>
      </div>
      <CollapsibleContent className="flex flex-col gap-3 px-3 pb-3">
        <div className="flex max-w-sm flex-col gap-1.5">
          <span className="text-sm font-medium text-muted-foreground">Name</span>
          <Input
            value={name}
            onChange={(e) => {
              setName(e.target.value)
              setDirty(true)
            }}
            placeholder="e.g. Standard vet day"
          />
        </div>

        <div className="flex flex-col gap-2">
          {blockDrafts.map((draft, i) => (
            <BlockRow
              key={draft.key}
              filterKey={draft.key}
              block={draft.block}
              onChange={(b) => setBlock(i, b)}
              onRemove={blockDrafts.length > 1 ? () => removeBlock(i) : undefined}
            />
          ))}
          <Button variant="outline" className="w-fit" onClick={addBlock}>
            <Plus className="size-4" />
            Add block
          </Button>
        </div>
      </CollapsibleContent>
    </Collapsible>
  )
}

type BlockMode = 'allowlist' | 'exclusion'

/** Rebuilds a block with new base fields (name/start/end), preserving its allow/exclude variant. */
function withBlockBase(
  block: ScheduleBlock,
  base: { name: string; startTime: string; endTime: string }
): ScheduleBlock {
  return block.appointmentTypesExcluded !== undefined
    ? { ...base, appointmentTypesExcluded: block.appointmentTypesExcluded }
    : { ...base, appointmentTypesAllowed: block.appointmentTypesAllowed }
}

interface BlockRowProps {
  /** Unique key namespacing this row's multiselect checkbox ids on the page. */
  filterKey: string
  block: ScheduleBlock
  onChange: (block: ScheduleBlock) => void
  /** Omitted when this is the template's only block (a template needs at least one). */
  onRemove?: () => void
}

/**
 * A single block's editor: name, time range, an allow/exclude mode toggle, and
 * the appointment-type multiselect. `ScheduleBlock` is a discriminated union —
 * exactly one of `appointmentTypesAllowed` / `appointmentTypesExcluded` is set —
 * so the mode is derived from which key is present rather than stored separately.
 */
function BlockRow({ filterKey, block, onChange, onRemove }: BlockRowProps) {
  const mode: BlockMode = block.appointmentTypesExcluded !== undefined ? 'exclusion' : 'allowlist'
  // Exactly one of these is defined per the ScheduleBlock union, but TS can't narrow
  // `block` from the already-computed `mode`, so fall back to [] for the other case.
  const selectedTypes =
    mode === 'allowlist'
      ? (block.appointmentTypesAllowed ?? [])
      : (block.appointmentTypesExcluded ?? [])

  const setMode = (next: BlockMode) => {
    if (next === mode) return
    const base = { name: block.name, startTime: block.startTime, endTime: block.endTime }
    // Switching modes resets the type list — a populated allowlist and a populated
    // exclusion list mean opposite things, so carrying the list across would silently
    // invert its meaning.
    onChange(
      next === 'allowlist'
        ? { ...base, appointmentTypesAllowed: [] }
        : { ...base, appointmentTypesExcluded: [] }
    )
  }

  const setTypes = (values: string[]) => {
    const ids = values as AppointmentTypeId[]
    const base = { name: block.name, startTime: block.startTime, endTime: block.endTime }
    onChange(
      mode === 'allowlist'
        ? { ...base, appointmentTypesAllowed: ids }
        : { ...base, appointmentTypesExcluded: ids }
    )
  }

  return (
    <div className="flex flex-col gap-2 rounded-md border p-2.5 sm:flex-row sm:flex-wrap sm:items-end sm:gap-3">
      <div className="flex min-w-32 flex-1 flex-col gap-1.5">
        <span className="text-sm font-medium text-muted-foreground">Block name</span>
        <Input
          value={block.name}
          onChange={(e) =>
            onChange(
              withBlockBase(block, {
                name: e.target.value,
                startTime: block.startTime,
                endTime: block.endTime,
              })
            )
          }
          placeholder="e.g. Morning consults"
        />
      </div>

      <div className="flex flex-col gap-1.5">
        <span className="text-sm font-medium text-muted-foreground">Start</span>
        <Input
          type="time"
          className="w-28"
          value={block.startTime}
          onChange={(e) =>
            onChange(
              withBlockBase(block, {
                name: block.name,
                startTime: e.target.value,
                endTime: block.endTime,
              })
            )
          }
        />
      </div>

      <div className="flex flex-col gap-1.5">
        <span className="text-sm font-medium text-muted-foreground">End</span>
        <Input
          type="time"
          className="w-28"
          value={block.endTime}
          onChange={(e) =>
            onChange(
              withBlockBase(block, {
                name: block.name,
                startTime: block.startTime,
                endTime: e.target.value,
              })
            )
          }
        />
      </div>

      <div className="flex flex-col gap-1.5">
        <span className="text-sm font-medium text-muted-foreground">Mode</span>
        <ToggleGroup
          value={[mode]}
          onValueChange={(values) => {
            // Base UI's ToggleGroup can report an empty array on deselect; ignore it
            // so exactly one of "Allow only" / "Exclude" is always active.
            const next = values[0]
            if (next) setMode(next as BlockMode)
          }}
          variant="outline"
          size="sm"
        >
          <ToggleGroupItem value="allowlist">Allow only</ToggleGroupItem>
          <ToggleGroupItem value="exclusion">Exclude</ToggleGroupItem>
        </ToggleGroup>
      </div>

      <div className="flex min-w-48 flex-1 flex-col gap-1.5">
        <span className="text-sm font-medium text-muted-foreground">
          {mode === 'allowlist' ? 'Allowed types (empty = all)' : 'Excluded types'}
        </span>
        <MultiSelectFilter
          filterKey={filterKey}
          options={APPOINTMENT_TYPE_OPTIONS}
          selected={selectedTypes}
          onChange={setTypes}
          placeholder={mode === 'allowlist' ? 'All types' : 'None excluded'}
          searchable
          allowClear
        />
      </div>

      {onRemove && (
        <Button
          variant="ghost-destructive"
          size="icon-sm"
          onClick={onRemove}
          aria-label="Remove block"
        >
          <X className="size-4" />
        </Button>
      )}
    </div>
  )
}
